import { useEffect, useRef, useState } from "react";

export interface RecurrenceToastState {
  readonly message: string;
  readonly applyAllLabel: string;
  readonly onUndo: () => void;
  readonly onApplyAll: () => void;
}

interface RecurrenceActionToastProps {
  readonly toast: RecurrenceToastState | null;
  readonly onDismiss: () => void;
}

const DURATION_MS = 7000;

export function RecurrenceActionToast({
  toast,
  onDismiss,
}: RecurrenceActionToastProps) {
  const toastId = useRef(0);
  const prevToast = useRef(toast);
  if (toast && toast !== prevToast.current) toastId.current++;
  prevToast.current = toast;
  const toastKey = String(toastId.current);
  const [progress, setProgress] = useState(100);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (!toast) return;
    const start = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, 100 - (elapsed / DURATION_MS) * 100);
      setProgress(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
        setExiting(true);
        setTimeout(onDismiss, 200);
      }
    }, 50);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- toast identity changes too often, toastKey is stable
  }, [toastKey, onDismiss]);

  if (!toast) return null;

  return (
    <div
      key={toastKey}
      className={`fixed bottom-20 md:bottom-6 left-1/2 -translate-x-1/2 z-50 ${exiting ? "animate-toast-exit" : "animate-slide-up"}`}
    >
      <div
        className="relative bg-neutral-900 text-neutral-50 rounded-xl shadow-xl overflow-hidden min-w-72 max-w-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 pt-3 pb-2">
          <p className="text-[10px] uppercase tracking-wide text-neutral-500 font-semibold">Événement récurrent</p>
          <p className="text-sm font-medium mt-0.5">{toast.message}</p>
        </div>

        <div className="flex flex-col gap-1 px-3 pb-3">
          <button
            type="button"
            onClick={() => {
              toast.onApplyAll();
              onDismiss();
            }}
            className="w-full text-left px-3 py-2 rounded-lg border border-red-500/30 hover:bg-red-500/10 transition-colors"
          >
            <span className="text-sm font-medium text-red-400">{toast.applyAllLabel}</span>
            <span className="block text-[11px] text-neutral-500">Supprime tous les jours de cette série</span>
          </button>

          <button
            type="button"
            onClick={() => {
              toast.onUndo();
              onDismiss();
            }}
            className="w-full px-3 py-2 rounded-lg border border-neutral-700 hover:bg-neutral-800 transition-colors text-sm font-medium text-neutral-300"
          >
            Annuler
          </button>
        </div>

        <div className="h-0.5 bg-neutral-700">
          <div
            className="h-full bg-accent transition-all ease-linear"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
}
